import { DialogComponent } from '@theme/dialog';
import { CartUpdateEvent } from '@theme/events';

class ProductSamplesDrawer extends DialogComponent {
    requiredRefs = ['dialog'];

    constructor() {
        super();

        this.cards = [];
        this.clearButton = null;
        this.addButton = null;
        this.totalElement = null;
        this.scrollArea = null;
        this.handleScrollAreaWheel = this.handleScrollAreaWheel.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();

        if (this.dataset.productSamplesInitialized === 'true') return;
        this.dataset.productSamplesInitialized = 'true';

        this.cards = Array.from(this.querySelectorAll('[data-sample-card]'));
        this.clearButton = this.querySelector('[data-samples-clear]');
        this.addButton = this.querySelector('[data-samples-add]');
        this.totalElement = this.querySelector('[data-samples-total]');
        this.separatorElement = this.querySelector('[data-samples-separator]');

        this.scrollArea = this.querySelector('[data-samples-scroll-area]');

        this.scrollArea?.addEventListener('wheel', this.handleScrollAreaWheel, {
            passive: false,
        });

        this.clearButton?.addEventListener('click', () => this.clearSelection());
        this.addButton?.addEventListener('click', () => this.addSelectedSamplesToCart());

        this.cards.forEach((card) => {
            card.addEventListener('click', () => this.toggleCard(card));
        });

        this.updateState();
    }

    disconnectedCallback() {
        super.disconnectedCallback?.();

        this.scrollArea?.removeEventListener('wheel', this.handleScrollAreaWheel);
    }

    handleScrollAreaWheel(event) {
        const scroller = this.scrollArea;

        if (!scroller) return;

        const canScroll = scroller.scrollHeight > scroller.clientHeight;

        if (!canScroll) return;

        event.preventDefault();
        event.stopPropagation();

        scroller.scrollTop += event.deltaY;
    }

    toggleCard(card) {
        if (card.disabled || card.dataset.available !== 'true') return;

        const isSelected = !card.classList.contains('is-selected');

        card.classList.toggle('is-selected', isSelected);
        card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

        this.updateState();
    }

    clearSelection() {
        this.cards.forEach((card) => {
            card.classList.remove('is-selected');
            card.setAttribute('aria-pressed', 'false');
        });

        this.updateState();
    }

    getSelectedCards() {
        return this.cards.filter((card) => card.classList.contains('is-selected'));
    }

    updateState() {
        const selectedCards = this.getSelectedCards();
        const selectedCount = selectedCards.length;
        this.classList.toggle('has-selection', selectedCount > 0);

        const total = selectedCards.reduce((sum, card) => {
            return sum + Number(card.dataset.price || 0);
        }, 0);

        if (this.clearButton) {
            this.clearButton.hidden = selectedCount === 0;
        }

        if (this.addButton) {
            this.addButton.disabled = selectedCount === 0;
        }

        if (this.separatorElement) {
            this.separatorElement.hidden = selectedCount === 0;
        }

        if (this.totalElement) {
            this.totalElement.hidden = selectedCount === 0;
            this.totalElement.textContent = selectedCount > 0 ? this.formatMoney(total) : '';
        }
    }

    async addSelectedSamplesToCart() {
        const selectedCards = this.getSelectedCards();

        if (!selectedCards.length || !this.addButton) return;

        const items = selectedCards.map((card) => ({
            id: Number(card.dataset.variantId),
            quantity: 1,
        }));

        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionsToUpdate = [];

        cartItemsComponents.forEach((item) => {
            if (item instanceof HTMLElement && item.dataset.sectionId) {
                sectionsToUpdate.push(item.dataset.sectionId);
            }
        });

        this.addButton.disabled = true;
        this.addButton.classList.add('is-loading');

        try {
            const addResponse = await fetch(Theme.routes.cart_add_url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({
                    items,
                    sections: sectionsToUpdate.join(','),
                    sections_url: window.location.pathname,
                }),
            });

            const addData = await addResponse.json();

            if (!addResponse.ok || addData.status) {
                throw new Error(addData.description || addData.message || 'Unable to add samples to cart');
            }

            const cartResponse = await fetch(Theme.routes.cart_url + '.js', {
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                },
            });

            const cart = await cartResponse.json();

            document.dispatchEvent(
                new CartUpdateEvent(cart, 'product-samples-drawer', {
                    itemCount: Number(cart.item_count) || 0,
                    source: 'product-samples-drawer',
                    sections: addData.sections || {},
                })
            );

            this.clearSelection();
            await this.closeDialog();
        } catch (error) {
            console.error(error);
        } finally {
            this.addButton.classList.remove('is-loading');
            this.updateState();
        }
    }

    formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            return window.Shopify.formatMoney(cents, Theme.moneyFormat || '${{amount}}');
        }

        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
}

if (!customElements.get('product-samples-drawer')) {
    customElements.define('product-samples-drawer', ProductSamplesDrawer);
}